<template>
  <div class="materijali-view">
    <header class="materijali-view__header">
      <div class="materijali-view__header-top">
        <h2 class="materijali-view__title">Materijali</h2>
        <button type="button" class="materijali-view__btn-create" @click="openCreateForm">
          <span class="material-symbols-outlined">add</span>
          Add new material
        </button>
      </div>
    </header>

    <div class="materijali-view__body">
      <div class="materijali-view__filters">
        <button
          v-for="category in categoryOptions"
          :key="category"
          type="button"
          class="materijali-view__filter-btn"
          :class="{ 'materijali-view__filter-btn--active': selectedCategories.includes(category) }"
          @click="toggleCategory(category)"
        >
          {{ category }}
        </button>
      </div>
      <div class="materijali-view__grid">
        <div
          v-for="material in filteredMaterials"
          :key="material.id"
          class="materijali-view__card"
          @click="goToMaterial(material)"
        >
          <button
            type="button"
            class="materijali-view__card-delete"
            title="Delete material"
            aria-label="Delete material"
            @click.stop="deleteMaterial(material)"
          >
            <span class="material-symbols-outlined">delete</span>
          </button>
          <div
            class="materijali-view__card-image"
            :style="cardImageStyle(material)"
          />
          <div class="materijali-view__card-content">
            <div class="materijali-view__card-header">
              <h3 class="materijali-view__card-title">{{ material.name }}</h3>
              <span
                v-if="material.tag"
                class="materijali-view__card-tag"
                :class="tagClassObject(material)"
                :style="tagInlineStyle(material)"
              >
                {{ material.tag }}
              </span>
            </div>
          </div>
        </div>

        <button
          type="button"
          class="materijali-view__card materijali-view__card--add"
          @click="openCreateForm"
        >
          <div class="materijali-view__card-add-content">
            <span class="material-symbols-outlined materijali-view__card-add-icon">add</span>
            <span class="materijali-view__card-add-title">Add New Material</span>
            <span class="materijali-view__card-add-subtitle">Start organizing a new family</span>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { Message, MessageBox } from 'element-ui'

const CATEGORY_OPTIONS = []

export default {
  name: 'Materijali',

  data () {
    return {
      selectedCategories: [],
      categoryOptionsStatic: CATEGORY_OPTIONS
    }
  },

  computed: {
    ...mapGetters('materijali', ['materials']),

    categoryOptions () {
      const fromData = (this.materials || []).reduce((acc, m) => {
        const cats = Array.isArray(m.materialCategories) ? m.materialCategories : []
        cats.forEach((c) => { if (c && !acc.includes(c)) acc.push(c) })
        return acc
      }, [])
      const combined = [...this.categoryOptionsStatic]
      fromData.forEach((c) => { if (c && !combined.includes(c)) combined.push(c) })
      return combined.length ? combined : this.categoryOptionsStatic
    },

    filteredMaterials () {
      if (this.selectedCategories.length === 0) return this.materials
      return this.materials.filter((m) => {
        const cats = Array.isArray(m.materialCategories) ? m.materialCategories : []
        return this.selectedCategories.some((c) => cats.includes(c))
      })
    }
  },

  mounted () {
    this.$store.dispatch('materijali/load')
  },

  methods: {
    async deleteMaterial (material) {
      try {
        await MessageBox.confirm(
          `Delete material "${material.name}"?`,
          'Delete material',
          {
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            type: 'warning'
          }
        )
        await this.$store.dispatch('materijali/deleteMaterial', material.id)
        Message.success('Material deleted.')
      } catch {}
    },

    cardImageStyle (material) {
      if (material.squareTile) {
        return {
          backgroundImage: `url(${material.squareTile})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#f1f5f9'
        }
      }
      return { background: material.imageGradient }
    },
    toggleCategory (category) {
      const idx = this.selectedCategories.indexOf(category)
      if (idx === -1) {
        this.selectedCategories.push(category)
      } else {
        this.selectedCategories.splice(idx, 1)
      }
    },
    tagClassObject (material) {
      const color = material.tagColor || material.tagClass || ''
      if (color && /^#[0-9a-fA-F]{6}$/.test(color)) return {}
      const cls = material.tagClass || 'gray'
      return { [`materijali-view__card-tag--${cls}`]: true }
    },
    tagInlineStyle (material) {
      const color = material.tagColor || material.tagClass || ''
      if (color && /^#[0-9a-fA-F]{6}$/.test(color)) {
        return { backgroundColor: color, color: '#fff' }
      }
      return {}
    },
    openCreateForm () {
      this.$router.push({ name: 'MaterijalEdit', params: { materialId: 'new' }, query: { new: '1' } })
    },

    goToMaterial (material) {
      this.$router.push({ name: 'MaterijalEdit', params: { materialId: material.id } })
    }
  }
}
</script>

<style lang="scss" scoped>
.materijali-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  font-family: 'Manrope', sans-serif;
}

.materijali-view__header {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(24px);
  padding: 2rem 3rem;
  flex-shrink: 0;
  border-bottom: 1px solid #f1f5f9;
}

.materijali-view__header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.materijali-view__title {
  font-size: 2.25rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: #0f172a;
  margin: 0;
}

.materijali-view__btn-create {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 3rem;
  padding: 0.75rem 1.5rem;
  box-sizing: border-box;
  background: #1a1a1a;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  .material-symbols-outlined {
    font-size: 0.875rem;
  }
  &:hover {
    background: #334155;
  }
}

.materijali-view__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 2rem 3rem;
}

.materijali-view__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.materijali-view__filter-btn {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #0f172a;
  }
  &--active {
    background: #3b82f6;
    border-color: #3b82f6;
    color: #fff;
    &:hover {
      background: #2563eb;
      border-color: #2563eb;
      color: #fff;
    }
  }
}

.materijali-view__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
}

.materijali-view__card {
  background: #fff;
  border-radius: 1rem;
  border: 1px solid #f1f5f9;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: box-shadow 0.15s;
  text-align: left;
  display: flex;
  flex-direction: column;
  position: relative;
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
}

.materijali-view__card-delete {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 1;
  width: 2rem;
  height: 2rem;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 0.5rem;
  color: #fff;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s, background 0.15s;
  .material-symbols-outlined {
    font-size: 1.125rem;
  }
  &:hover {
    background: rgba(220, 38, 38, 0.9);
  }
}

.materijali-view__card:hover .materijali-view__card-delete {
  opacity: 1;
}

.materijali-view__card-image {
  aspect-ratio: 1;
  width: 100%;
  border-radius: 1rem 1rem 0 0;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #f1f5f9;
}

.materijali-view__card-content {
  padding: 1.25rem;
}

.materijali-view__card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.materijali-view__card-title {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  flex: 1;
  min-width: 0;
}

.materijali-view__card-tag {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  flex-shrink: 0;
  &--gray {
    background: #f1f5f9;
    color: #475569;
  }
  &--orange {
    background: #f97316;
    color: #fff;
  }
  &--blue-gray {
    background: #e2e8f0;
    color: #475569;
  }
  &--dark {
    background: #334155;
    color: #fff;
  }
  &--purple {
    background: #6366f1;
    color: #fff;
  }
}

.materijali-view__card--add {
  border: 2px dashed #e2e8f0;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  &:hover {
    border-color: #cbd5e1;
    background: #fafafa;
  }
}

.materijali-view__card-add-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
}

.materijali-view__card-add-icon {
  font-size: 2.5rem;
  color: #94a3b8;
  width: 3.5rem;
  height: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  border-radius: 50%;
}

.materijali-view__card-add-title {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
}

.materijali-view__card-add-subtitle {
  font-size: 0.8125rem;
  color: #94a3b8;
}

.materijali-view__form {
  padding: 0.25rem 0;
}

.materijali-view__form-group {
  margin-bottom: 1.5rem;
  &:last-of-type {
    margin-bottom: 0;
  }
}

.materijali-view__form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.5rem;
}

.materijali-view__form-input {
  width: 100%;
}
</style>
